import React from "react";

/**
 * Inline single-stroke SVG icons matching the design system's Lucide / Phosphor
 * line family (1.5–2px stroke, no fill, 24px frame). Ported verbatim from the
 * design package's kiosk.jsx so the production bundle never depends on a
 * runtime icon CDN — every glyph here is reachable offline.
 */
export type IconName =
  | "search"
  | "x"
  | "check"
  | "thumbs-up"
  | "thumbs-down"
  | "minus"
  | "plus"
  | "pencil"
  | "arrow-right"
  | "arrow-left"
  | "chevron-up"
  | "chevron-down"
  | "keypad"
  | "help"
  | "lock"
  | "translate";

export interface IconProps {
  name: IconName;
  size?: number;
  color?: string;
}

export const Icon: React.FC<IconProps> = ({
  name,
  size = 22,
  color = "currentColor"
}) => {
  const stroke = {
    stroke: color,
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    fill: "none"
  };
  switch (name) {
    case "search":
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
      );
    case "x":
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      );
    case "check":
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
          <path d="M5 12l5 5L20 7" />
        </svg>
      );
    case "thumbs-up":
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
          <path d="M7 22V10" />
          <path d="M14 4V2c0-1 1-2 2-2v0c2 0 3 1 3 3v5h3a2 2 0 0 1 2 2l-2 8a3 3 0 0 1-3 2H7" />
          <path d="M4 10h3v12H4z" />
        </svg>
      );
    case "thumbs-down":
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
          <path d="M17 2v12" />
          <path
            d="M10 20v2c0 1-1 2-2 2v0c-2 0-3-1-3-3v-5H2a2 2 0 0 1-2-2l2-8a3 3 0 0 1 3-2h12"
            transform="translate(2 0)"
          />
          <path d="M20 14h-3V2h3z" />
        </svg>
      );
    case "minus":
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
          <path d="M5 12h14" />
        </svg>
      );
    case "plus":
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
          <path d="M12 5v14M5 12h14" />
        </svg>
      );
    case "pencil":
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
          <path d="M4 20h4l10-10-4-4L4 16v4z" />
          <path d="M14 6l4 4" />
        </svg>
      );
    case "arrow-right":
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
          <path d="M5 12h14M13 5l7 7-7 7" />
        </svg>
      );
    case "arrow-left":
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
          <path d="M19 12H5M11 5l-7 7 7 7" />
        </svg>
      );
    case "chevron-up":
      // Outsized chevrons for the agenda picker side-rail page-up/down buttons
      // (LAC 5/18 feedback). Path drawn at viewBox center so it remains
      // visually balanced inside the round button at any size from 24-48.
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
          <path d="M6 15l6-6 6 6" />
        </svg>
      );
    case "chevron-down":
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      );
    case "keypad":
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
          <rect x="3" y="3" width="6" height="6" rx="1" />
          <rect x="15" y="3" width="6" height="6" rx="1" />
          <rect x="3" y="15" width="6" height="6" rx="1" />
          <rect x="15" y="15" width="6" height="6" rx="1" />
          <rect x="9" y="9" width="6" height="6" rx="1" />
        </svg>
      );
    case "help":
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
          <circle cx="12" cy="12" r="10" />
          <path d="M9.5 9a2.5 2.5 0 1 1 4 2c-1 .7-1.5 1.5-1.5 2.5" />
          <circle cx="12" cy="17.5" r="0.5" fill={color} />
        </svg>
      );
    case "lock":
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
          <rect x="4" y="11" width="16" height="10" rx="2" />
          <path d="M8 11V8a4 4 0 0 1 8 0v3" />
        </svg>
      );
    case "translate":
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
          <path d="M4 5h10" />
          <path d="M9 3v2c0 6-4 9-5 9" />
          <path d="M5 10c0 2 3 5 7 5" />
          <path d="M13 21l5-12 5 12" />
          <path d="M15 17h6" />
        </svg>
      );
    default:
      return null;
  }
};
